import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { logger } from '../utils/logger';
import { ArrowRight, TrendingUp } from 'lucide-react';

// Components
import SearchBar from '../components/home/SearchBar';
import QuickFilterChips from '../components/home/QuickFilterChips';
import DealerCard from '../components/home/DealerCard';
import FeaturedVehicleCard from '../components/home/FeaturedVehicleCard';
import ValuePropositions from '../components/home/ValuePropositions';
import ProcessSteps from '../components/home/ProcessSteps';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Home - Redesigned home screen for SelfDrive
 * Focus: Test drive booking, location-based search, clean CarGurus-inspired design
 */
const Home = () => {
  const navigate = useNavigate();

  // State
  const [dealers, setDealers] = useState([]);
  const [featuredVehicles, setFeaturedVehicles] = useState([]);
  const [popularVehicles, setPopularVehicles] = useState([]);
  const [activeFilters, setActiveFilters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);

  // Fetch initial data
  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [dealersResponse, featuredResponse, popularResponse] = await Promise.all([
        axios.get(`${API_URL}/dealers?sortBy=rating`),
        axios.get(`${API_URL}/vehicles?featured=true`),
        axios.get(`${API_URL}/vehicles?sortBy=popular`),
      ]);

      setDealers(dealersResponse.data.slice(0, 6)); // Top 6 dealers
      setFeaturedVehicles(featuredResponse.data.slice(0, 6));
      setPopularVehicles(popularResponse.data.slice(0, 6));
    } catch (error) {
      logger.error('Error fetching initial data', { message: error?.message });
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = async (searchData) => {
    const params = new URLSearchParams();

    if (searchData.make) params.append('make', searchData.make);
    if (searchData.model) params.append('model', searchData.model);
    if (searchData.bodyStyle) params.append('type', searchData.bodyStyle);
    if (searchData.location) params.append('location', searchData.location);
    if (searchData.availableToday) params.append('availability', 'today');

    // Navigate to vehicles page with search params
    navigate(`/vehicles?${params.toString()}`);
  };

  // Handle location request
  const handleLocationRequest = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          // In Phase 2, we'll use this to filter dealers by proximity
          alert('Location detected! In Phase 2, we\'ll show dealers near you.');
        },
        (error) => {
          console.error('Error getting location:', error);
          alert('Unable to get your location. Please enter a city or ZIP code.');
        }
      );
    } else {
      alert('Geolocation is not supported by your browser.');
    }
  };

  // Handle quick filter toggle
  const handleFilterToggle = async (filterId) => {
    const newFilters = activeFilters.includes(filterId)
      ? activeFilters.filter(f => f !== filterId)
      : [...activeFilters, filterId];

    setActiveFilters(newFilters);

    // Apply filters - navigate to vehicles page with appropriate params
    try {
      const params = new URLSearchParams();

      if (newFilters.includes('availableNow')) {
        params.append('availability', 'today');
      }

      if (newFilters.includes('mostPopular')) {
        params.append('sortBy', 'popular');
      }

      if (newFilters.includes('topRated')) {
        params.append('sortBy', 'rating');
        params.append('verifiedOnly', 'true');
      }

      if (newFilters.includes('lowMileage')) {
        params.append('maxMileage', '40000');
      }

      if (newFilters.includes('nearMe')) {
        handleLocationRequest();
        return;
      }

      // Navigate to vehicles page with filters applied
      if (params.toString()) {
        navigate(`/vehicles?${params.toString()}`);
      }
    } catch (error) {
      logger.error('Error applying filters', { message: error?.message });
    }
  };

  return (
    <div className="min-h-screen bg-app-bg-light dark:bg-app-bg">
      {/* Hero Section - professional blue gradient */}
      <section className="relative bg-gradient-to-br from-app-accent/10 via-blue-50 to-app-accent/5 dark:from-app-accent/10 dark:via-app-bg dark:to-app-accent/5 py-12 lg:py-20 overflow-hidden">
        {/* Subtle decorative elements */}
        <div className="absolute top-10 right-10 w-32 h-32 bg-app-accent/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-40 h-40 bg-app-accent/15 rounded-full blur-3xl"></div>

        <div className="max-w-7xl mx-auto px-4 relative">
          {/* Headline - professional and clear */}
          <div className="text-center mb-8">
            <h1 className="text-4xl lg:text-6xl font-bold text-app-text-light dark:text-app-text mb-4">
              Book Your Next Test Drive<br />
              <span className="bg-gradient-to-r from-app-accent to-blue-400 bg-clip-text text-transparent">Fast, Easy, On Your Schedule.</span>
            </h1>
            <p className="text-xl lg:text-2xl text-app-text-secondary-light dark:text-app-text-secondary max-w-3xl mx-auto">
              Find vehicles, check availability, and schedule your test drive in minutes.
            </p>
          </div>

          {/* CTAs - professional blue colors */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <button
              onClick={() => document.getElementById('search-section').scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 bg-gradient-to-r from-app-accent to-app-accent-hover hover:from-app-accent-hover hover:to-app-accent-active text-white rounded-2xl font-bold text-lg transition-all duration-300 hover:shadow-xl hover:shadow-app-accent/30 hover:scale-105 flex items-center justify-center gap-2"
            >
              Start Your Search
              <ArrowRight className="w-5 h-5" />
            </button>
            <button
              onClick={() => navigate('/vehicles')}
              className="px-8 py-4 bg-white dark:bg-app-bg-secondary hover:bg-blue-50 dark:hover:bg-app-bg-tertiary text-app-text-light dark:text-app-text border-2 border-app-accent/30 hover:border-app-accent rounded-2xl font-bold text-lg transition-all duration-300"
            >
              Browse All Vehicles
            </button>
          </div>

          {/* Search & Filter Module */}
          <div id="search-section" className="mb-8">
            <SearchBar onSearch={handleSearch} onLocationRequest={handleLocationRequest} />
          </div>

          {/* Quick Filter Chips */}
          <div className="flex justify-center">
            <QuickFilterChips
              activeFilters={activeFilters}
              onFilterToggle={handleFilterToggle}
            />
          </div>
        </div>
      </section>

      {/* Nearby Availability Section */}
      {dealers.length > 0 && (
        <section className="py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl lg:text-4xl font-bold text-app-text-light dark:text-app-text mb-2">
                  Dealerships Near You
                </h2>
                <p className="text-lg text-app-text-secondary-light dark:text-app-text-secondary">
                  Top-rated dealers with verified inventory
                </p>
              </div>
              <button
                onClick={() => navigate('/vehicles')}
                className="hidden lg:flex items-center gap-2 text-app-accent font-semibold hover:gap-3 transition-all"
              >
                View All
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-app-accent border-t-transparent"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {dealers.map(dealer => (
                  <DealerCard
                    key={dealer._id}
                    dealer={dealer}
                    distance={null} // Phase 2: Calculate actual distance
                    vehicleCount={0} // Can be fetched if needed
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Featured Vehicles Section */}
      {featuredVehicles.length > 0 && (
        <section className="py-12 lg:py-16 bg-gradient-to-br from-blue-50/50 to-white dark:from-app-bg-tertiary/20 dark:to-app-bg">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl lg:text-4xl font-bold text-app-text-light dark:text-app-text mb-2">
                  Featured Vehicles
                </h2>
                <p className="text-lg text-app-text-secondary-light dark:text-app-text-secondary">
                  Hand-picked vehicles available for test drives
                </p>
              </div>
              <button
                onClick={() => navigate('/vehicles?featured=true')}
                className="hidden lg:flex items-center gap-2 text-app-accent font-semibold hover:gap-3 transition-all"
              >
                View All Featured
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredVehicles.map(vehicle => (
                <FeaturedVehicleCard
                  key={vehicle._id}
                  vehicle={vehicle}
                  distance={null} // Phase 2: Calculate from dealer location
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Most Popular Vehicles Section */}
      {popularVehicles.length > 0 && (
        <section className="py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between mb-8">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-8 h-8 text-app-accent" />
                  <h2 className="text-3xl lg:text-4xl font-bold text-app-text-light dark:text-app-text">
                    Most Popular Test Drives
                  </h2>
                </div>
                <p className="text-lg text-app-text-secondary-light dark:text-app-text-secondary">
                  Trending vehicles with the most bookings
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {popularVehicles.map(vehicle => (
                <FeaturedVehicleCard
                  key={vehicle._id}
                  vehicle={vehicle}
                  distance={null}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Value Propositions */}
      <ValuePropositions />

      {/* Process Steps */}
      <ProcessSteps />

      {/* CTA Banner - professional blue */}
      <section className="py-12 lg:py-16 bg-gradient-to-br from-app-accent via-app-accent-hover to-blue-600 relative overflow-hidden">
        {/* Subtle background elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-400/20 rounded-full blur-3xl"></div>

        <div className="max-w-4xl mx-auto px-4 text-center relative">
          <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4">
            Ready to Test Something New?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Browse our inventory and book your next test drive today.
          </p>
          <button
            onClick={() => navigate('/vehicles')}
            className="px-10 py-5 bg-white text-app-accent rounded-2xl font-bold text-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 hover:bg-blue-50"
          >
            Start Your Search
          </button>
        </div>
      </section>
    </div>
  );
};

export default Home;
